import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

import { Amplify, Auth } from 'aws-amplify';
import awsExports from './aws-exports';
import 'bootstrap/dist/css/bootstrap.min.css';

// ── FITTWorks hub SSO ───────────────────────────────────────────────────
// Sign-in happens against the shared FITTWorks hub pool, not this app's own
// Amplify auth resource. These values are applied over aws-exports.js rather
// than stored in it: `amplify pull` regenerates that file from the lfapi
// backend and silently drops the hub pool and the whole oauth block (it comes
// back as `"oauth": {}`, which used to crash this file at boot). Everything
// else in aws-exports — AppSync, S3, the REST endpoint — is still authoritative.
//
// OPT-IN, NOT DEFAULT. `REACT_APP_AUTH_HUB` must be set for the hub to be applied at all:
// amplify.yml sets it on the staging branch, `yarn start:staging` sets it locally. leanfitt.com
// (branch `LfBranch`) does NOT set it, so prod keeps the pool in aws-exports.js
// (us-west-2_LLZTeB8Je, 253 real accounts).
//
// The gate is deliberately opt-in rather than opt-out: the hub values below default to the STAGING
// hub, so an unguarded block would have pointed production at us-west-2_PG0KIa36P (8 test accounts)
// the moment this branch shipped to LfBranch — every real user locked out, and Storage broken too
// because the staging identity pool does not trust the prod user pool.
//
// Cutting production over to the hub is then an environment change, not a code change: set
// REACT_APP_AUTH_HUB plus the four REACT_APP_HUB_* values on the prod branch. Do NOT set them until
// the prod hub (us-west-2_DEtpsXop8) actually has the UserMigration and PreSignUp triggers and a
// migration ledger — as of 2026-09-08 it has neither, and zero users.
const HUB_ENABLED = !!process.env.REACT_APP_AUTH_HUB;

const HUB = {
  userPoolId: process.env.REACT_APP_HUB_POOL_ID || "us-west-2_PG0KIa36P",
  webClientId: process.env.REACT_APP_HUB_CLIENT_ID || "6tireef66t631acsd1n8390glq",
  // Must be an identity pool that trusts the user pool above — the credential
  // exchange checks the JWT issuer, so a mismatched pair leaves Storage with no
  // credentials at all rather than with reduced ones.
  identityPoolId:
    process.env.REACT_APP_HUB_IDENTITY_POOL_ID ||
    "us-west-2:6481c27e-824f-4fe3-bf17-b60c72a52822",
  domain: process.env.REACT_APP_HUB_AUTH_DOMAIN || "staging-auth.fittworks.app",
};

if (HUB_ENABLED) {
  awsExports.aws_user_pools_id = HUB.userPoolId;
  awsExports.aws_user_pools_web_client_id = HUB.webClientId;
  awsExports.aws_cognito_identity_pool_id = HUB.identityPoolId;
  awsExports.federationTarget = "COGNITO_USER_POOLS";

  awsExports.oauth = {
    domain: HUB.domain,
    scope: ["aws.cognito.signin.user.admin", "email", "openid", "phone", "profile"],
    // Every leanfitt callback registered on the hub client is the bare origin, so
    // deriving it is both correct and simpler than the comma-joined list
    // aws-exports used to ship — Amplify v5 sends that string verbatim and Cognito
    // rejects it with redirect_mismatch.
    redirectSignIn: `${window.location.origin}/`,
    redirectSignOut: `${window.location.origin}/`,
    responseType: "code",
  };
}

Amplify.configure(awsExports);

// Amplify defaults Auth.signIn to USER_SRP_AUTH, and Cognito does NOT invoke the UserMigration
// trigger on SRP — the password never reaches the service in a form the trigger could verify. The
// main login uses the hosted UI so it was never affected, but the invite flow signs in IN-APP, so a
// legacy account accepting an invitation could not migrate: it simply failed to authenticate.
//
// This must go through Auth.configure, NOT an aws-exports key. `aws_cognito_authentication_flow_type`
// does not appear anywhere in @aws-amplify/core's parseAWSExports (v5.2.1) — setting it on awsExports
// is silently ignored, which is exactly the kind of no-op that looks fixed and is not. Auth.js reads
// `this._config.authenticationFlowType` and calls setAuthenticationFlowType when it is present.
//
// The hub client allows this flow (ExplicitAuthFlows contains ALLOW_USER_PASSWORD_AUTH). The password
// travels over TLS to Cognito and is never stored here.
Auth.configure({ authenticationFlowType: "USER_PASSWORD_AUTH" });

// Maintenance mode is gated per-route in AppRouter (REACT_APP_MAINTENANCE), so public/landing pages
// stay reachable while login and the authenticated app are held on the maintenance message.
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
